/**
 * 处理浏览框高级搜索设置的字段
 */

import { ReactNode } from "react";
import { classnames } from "@weapp/utils";
import { formItemClsPrefix, searchAdvancedClsPrefix } from "../../../../constants";
import { AnyObj } from "../../../../types/common";
import { FormCustomLayoutProps, FormLayoutProps, FormLayoutType, FormStoreType, FormItemSingleProps } from "../../../form";
import { FormItemProps, FormItemWrapperProps, FormSingleItemProps } from "../../../form-item";
import { SearchAdvancedInitAllDatas, SearchAdvancedStoreType } from "../../store/Store";
import { mSearchAdvancedClsPrefix } from "../../../../constants/index.m";
import FormSwitch from "../../../form-switch";
import { FieldProps } from "../../types";
import { isObservableArray } from "mobx";
import { getInitField } from "..";
import { isEmpty, isNil, needSync } from "../../../../utils";
const { getRequired } = FormSwitch;


export const SearchAdLayoutPropsName = 'SearchAdLayoutProps'; // 后面改为从weapp/profile中获取

// 是否其他分组字段
export const isCustomField = (dataKey: string) => dataKey.indexOf('_customField') >= 0;
// 去除后缀
export const removeSuffix = (dataKey: string, suffix?: string) => dataKey.slice(0, dataKey.indexOf(suffix || '_customField'));

// 获取其他分组字段后缀
export const getCustomFieldId = (dataKey: string, key: string) => {
  const startIndex = dataKey.indexOf('_customField') + 1;
  const endIndex = dataKey.length - key.length;
  return dataKey.slice(startIndex, endIndex);
}
// 是否只读设置字段
export const isReadOnlySetting = (dataKey: string) => dataKey.indexOf('_readonly') >= 0;
// 是否只读设置字段
export const isVisibleSetting = (dataKey: string) => dataKey.indexOf('_visible') >= 0;
// 是否只读设置字段
export const isSortSetting = (dataKey: string) => dataKey.indexOf('_sort') >= 0;

/**
 * 是否设置高级搜索默认配置
 * 1.设置后必有常用分组的配置数据
 * 2.设置后必有readonly等配置
 * 
 * @param data 高级搜索回显数据
 * @returns 
 */
export const isSetting = (data: AnyObj, content: SearchAdvancedInitAllDatas) => {
  const flag = Object.keys(data).find((key) => isReadOnlySetting(key) || isVisibleSetting(key) || isSortSetting(key));
  // data为空，判断是否存在禁用的配置
  if (!flag) return content?.layout?.find((row) => row?.find((col) => col.disable))
  return flag;
}

/**
 * 禁用其他分组条件(设置过默认搜索条件的): layout配置了disable, 需同时禁用常用分组、其他分组、常用分组设置条件
 * @param store 
 * @param customLayout 
 * @param disableFieldsKeys 
 */
export const doDisabledOtherGroupFields = (store: SearchAdvancedStoreType, customLayout: FormCustomLayoutProps, disableFieldsKeys: Set<string>) => {
  if (disableFieldsKeys && disableFieldsKeys.size > 0) {
    const customRows = customLayout?.otherGroup?.[0];
    if (!customRows) return;
    const nowCustomRows = customRows.map((col) => disableFieldsKeys.has(col.id) ? { ...col, disabled: true } : col)
    if (nowCustomRows.length > 0) {
      store.formStore.setAllCustomLayout({
        otherGroup: [nowCustomRows]
      }, true);
    }
  }
}
/**
 * 处理其他分组条件隐藏逻辑
 * @param layout 高级搜索layout布局
 * @param hideLayouts 隐藏常用分组组条件id集合
 * @returns 
 */
export const transLayout = (layout: FormLayoutType[], hideLayouts?: Set<string | undefined>, otherConfigs?: AnyObj) => {
  if ((hideLayouts && hideLayouts?.size > 0) || (otherConfigs?.disableFieldsKeys && otherConfigs?.disableFieldsKeys?.size > 0)) {
    return layout?.map((row) => {
      return row.map((col) => {
        col.disabled = hideLayouts?.has(col.id) || otherConfigs?.disableFieldsKeys?.has(col.id);
        return col;
      })
    })
  }
  return layout;
}

/**
 * no.2179860 高级搜索支持必填校验
 * 字段属性规则：必填的字段不可禁用、只读、隐藏
 * no.2312845 修复自定义高级搜索设置常用分组默认条件，用户前台通过个人设置隐藏该字段，字段不生效的问题
 * 前台隐藏，后台设置的默认条件也要生效
 * @param data 
 * @param content 
 * @returns 
 */
const changeFieldAttr = (_data: AnyObj, _content: SearchAdvancedInitAllDatas, store: SearchAdvancedStoreType) => {
  let layoutId = new Set<string>([]), data = { ..._data }, content = { ..._content };
  Object.keys(content?.items)?.forEach((k) => {
    if (getRequired(content?.items[k])) {
      data[`${k}_visible`] = true;
      data[`${k}_readonly`] = false;
      const { nowlayout } = store.transDatasStore.getLayoutFromItemKey(k, content.layout, { data, value: data[k] }) as unknown as FormLayoutProps;
      if ((nowlayout?.disable || nowlayout?.disabled) && nowlayout?.id) layoutId.add(nowlayout?.id);
    }
  })
  const layout = content?.layout?.map((row) => {
    return row?.map((col) => {
      // if (col.hide) {
      //   const allItems = getItems(col);
      //   allItems.forEach((k) => {
      //     delete data[k];
      //   })
      // }
      return layoutId?.has(col.id) ? { ...col, disabled: false, disable: false }: col;
    })
  })
  return { data, content: { ...content, layout } };
}
const changeCommonLayout = (dataKey: string, data: AnyObj, content: SearchAdvancedInitAllDatas, store: SearchAdvancedStoreType, otherParams: AnyObj) => {
  // 常用分组隐藏和排序
  const { commonlayoutIds, commonLayoutConfig, customLayoutIds, customLayoutIdConfigs } = otherParams;
  if (dataKey.indexOf('_customField') < 0) {
    const { nowlayout } = store.transDatasStore.getLayoutFromItemKey(dataKey, content.layout, { data, value: data[dataKey] }) as unknown as FormLayoutProps;
    if (nowlayout) {
      if (!commonlayoutIds.has(nowlayout.id)) {
        commonlayoutIds.add(nowlayout.id);
        let hide: boolean | undefined = undefined; // 后台未设置的情况
        nowlayout?.items?.forEach((k: string) => {
          if (!hide) hide = data[`${k}_visible`] === false; // 多个字段，其中一个隐藏即为不展示
        })
        commonLayoutConfig.push({
          layoutId: nowlayout.id,
          open: isNil(hide) ? nowlayout.hide === false : !hide,
          sort: data[`${dataKey}_sort`],
        })
      } else if (needSync(`${dataKey}_sort`, data)) {
        const temp = commonLayoutConfig.find((layout: { layoutId: any; }) => layout.layoutId === nowlayout.id);
        temp.sort = data[`${dataKey}_sort`];
      }
    }
  } else {
    // 其他分组排序
    const layoutId = dataKey.slice(dataKey.indexOf('_customField') + 1);
    const sort = Number.parseInt(data[`${dataKey}_sort`]);
    if (data[`${dataKey}_visible`] && !customLayoutIds.find((layid: string) => layid === layoutId)) {
      customLayoutIds[sort] = layoutId;
    }
    // if (data[`${dataKey}_visible`]) { // 默认展示
      !customLayoutIdConfigs[sort] && (customLayoutIdConfigs[sort] = []);
      customLayoutIdConfigs[sort].push(layoutId);
    // }

  }
  return { commonlayoutIds, commonLayoutConfig, customLayoutIds, customLayoutIdConfigs };
}

/**
 * 隐藏(visible)、只读(readonly)、排序(sort)处理
 * @param data 
 * @returns 
 */
const transDatas = (_data: AnyObj, _content: SearchAdvancedInitAllDatas, store: SearchAdvancedStoreType, needTransBrowserConfigLayout?: boolean, isReset?: boolean): AnyObj => {
  const issetting = _data ? isSetting(_data, _content) : false;
  if (issetting && _data) {
    const { data, content } = changeFieldAttr(_data, _content, store);
    !isReset && store.browserConfigStore.setState({
      allDatas: { ...content },
      needTransBrowserConfigLayout,
    })
    /**
     * wholeRowReadOnlyFieldsKeys: 整行字段只读标记：有一个字段只读，则整行字段只读标记
     * disableFieldsKeys: 禁用字段key值集合，常用分组、其他分组、常用条件设置均禁用
     */
    let wholeRowReadOnlyFieldsKeys: Map<string, string[]> = new Map([]), disableFieldsKeys: Set<string> = new Set([]), disableItemsKeys: Set<string> = new Set([]),
      itemColMapping = new Map<string, AnyObj>();
    const allFields: FieldProps[] = [], commonCondition: FieldProps[] = [];
    const layout = content.layout?.map((row) => {
      return row.map((col) => {
        // 过滤禁用搜索条件设置项，单独存储使用，避免影响Form
        if (col.disable) {
          disableFieldsKeys.add(col.id);
          col.disableCustomConditions = true; // 禁用常用条件设置
          col.disabled = true; // 禁用常用条件
          col.items?.forEach((itemKey) => {
            disableItemsKeys.add(itemKey);
            itemColMapping.set(itemKey, { id: col.id, label: col.label });
          });
          if (col?.cascadeRules) {
            Object.values(col?.cascadeRules)?.forEach((value) => {
              value && Object.values(value)?.forEach((vals) => {
                const val = vals as any;
                if (Array.isArray(val) || isObservableArray(val)) {
                  val?.forEach((k: string) => {
                    if (k) {
                      disableItemsKeys.add(k);
                      itemColMapping.set(k, { id: col.id, label: col.label });
                    }
                  })
                } else if (val?.items && (Array.isArray(val?.items) || isObservableArray(val?.items))) {
                  val?.items?.forEach((k: string) => {
                    if (k) {
                      disableItemsKeys.add(k);
                      itemColMapping.set(k, { id: col.id, label: col.label });
                    }
                  })
                }
              })
            })
          }
        }
        const temp = getInitField(col, content, store.isMobile) as FieldProps;
        let flag = false;
        if (temp.groupId === 'commonGroup') {
          if (!temp.disabled && needTransBrowserConfigLayout) {
            // 未设置全局禁用搜索条件，无个人常用条件设置的情况，以data内设置的visible为准
            temp.items?.forEach((itemKey: any) => {
              // 默认展示； 对标设置界面，有一个字段的visible表示隐藏，即整行隐藏
              flag = flag || (data[`${itemKey}_visible`] === false);
            })
          }
          if (flag) {
            commonCondition.push({ ...temp, hide: true });
          } else if (!temp.disabled) {
            commonCondition.push(temp);
          }
        }
        allFields.push(temp);
        // if (col.readonly !== false) {
        col.items?.forEach((itemKey) => {
          wholeRowReadOnlyFieldsKeys.set(itemKey, col.items);
          if (col?.cascadeRules) {
            Object.values(col?.cascadeRules)?.forEach((value) => {
              value && Object.values(value)?.forEach((vals) => {
                const val = vals as any;
                const oldItems = wholeRowReadOnlyFieldsKeys.get(itemKey);
                if (Array.isArray(val) || isObservableArray(val)) {
                  wholeRowReadOnlyFieldsKeys.set(itemKey, [...(oldItems || []), ...val]);
                  val?.forEach((v) => {
                    wholeRowReadOnlyFieldsKeys.set(v, [...(oldItems || []), ...val]);
                  })
                } else if (val?.items && (Array.isArray(val?.items) || isObservableArray(val?.items))) {
                  wholeRowReadOnlyFieldsKeys.set(itemKey, [...(oldItems || []), ...val?.items]);
                  val?.items?.forEach((v: string) => {
                    wholeRowReadOnlyFieldsKeys.set(itemKey, [...(oldItems || []), ...val?.items]);
                  })
                }
              })
            })
          }
        }); // 整行只读
        // }
        // delete col.disable;
        delete col.readonly;
        return col;
      })
    })

    let datas: AnyObj = {}, readOnlyItems: Set<string> = new Set([]), commonLayoutConfig: any[] = [], commonlayoutIds = new Set(), customLayoutIds: string[] = [], customLayoutIdConfigs: any[] = [], customHideLayouts: Set<string | undefined> = new Set([]), statusDatas: AnyObj = {};
    Object.keys(data).forEach((dataKey) => {
      // 只读、隐藏、排序的配置不算data回显内容
      if (isVisibleSetting(dataKey) || isReadOnlySetting(dataKey) || isSortSetting(dataKey)) {
        statusDatas = { ...statusDatas, [dataKey]: data[dataKey] };
        let suffix = '';
        if (isReadOnlySetting(dataKey)) suffix = '_readonly';
        else if (isVisibleSetting(dataKey)) suffix = '_visible';
        else if (isSortSetting(dataKey)) suffix = '_sort';
        const dakey = removeSuffix(dataKey, suffix);
        wholeRowReadOnlyFieldsKeys.get(dakey)?.forEach((fieldkeys) => {
          statusDatas = {
            ...statusDatas,
            [`${fieldkeys}${suffix}`]: data[dataKey],
          }
        });
        /**
         * 兼容其他分组空字段，仅返回状态数据(readonly, sort, visible)，未返回空的value，导致字段不展示的情况
         */
        if (isCustomField(dakey) && (!needSync(dakey, data) && !needSync(dakey, datas))) {
          const dk = removeSuffix(dakey);
          datas = {
            ...datas,
            [dakey]: store.getDefaultValue(content?.items?.[dk], dk),
          }
        }
        /* ------------------------------------------------------------------------------------- */
      }
      if (isReadOnlySetting(dataKey)) {
        data[dataKey] && readOnlyItems.add(dataKey.slice(0, dataKey.indexOf('_readonly')));
      } else if (!isVisibleSetting(dataKey) && !isSortSetting(dataKey)) {
        // 隐藏的字段需作为搜索条件
        datas = {
          ...datas,
          [`${dataKey}`]: data[dataKey],
        }
        const { commonlayoutIds: _commonlayoutIds, commonLayoutConfig: _commonLayoutConfig, customLayoutIds: _customLayoutIds, customLayoutIdConfigs: _customLayoutIdConfigs } = changeCommonLayout(dataKey, data, content, store, { commonlayoutIds, commonLayoutConfig, customLayoutIds, customLayoutIdConfigs });
        if (needTransBrowserConfigLayout) {
          commonlayoutIds = _commonlayoutIds;
          commonLayoutConfig = _commonLayoutConfig;
          customLayoutIds = _customLayoutIds;
          customLayoutIdConfigs = _customLayoutIdConfigs;
        } else {
          customLayoutIds = _customLayoutIds;
          customLayoutIdConfigs = _customLayoutIdConfigs; // 存在个人设置，仅需更新其他分组排序相关的配置
        }
        /*
          后台设置空条件数据，接口不会返回value(接口已确认无法处理)，需前端补充
          (仅处理其他分组即可，否则其他分组会展示常用分组对应的数据)
        */
        if (isCustomField(dataKey)) {
          const dakey = removeSuffix(dataKey);
          wholeRowReadOnlyFieldsKeys?.get(dakey)?.forEach((k) => {
            const otherKey = `${k}_${getCustomFieldId(dataKey, '')}`;
            k !== dakey && isNil(data[otherKey]) && isNil(datas[otherKey]) && (
              datas = {
                ...datas,
                [otherKey]: store?.getDefaultValue(content?.items?.[k], ''),
              }
            )
          })
        }
        /* ---------------------------------------------------------------------------------------------------- */
      } else if (isVisibleSetting(dataKey) && (isCustomField(dataKey) || needTransBrowserConfigLayout)) {
        // needTransBrowserConfigLayout 对应无个人设置的情况，有个人设置的情况，常用分组不在处理单个字段隐藏的逻辑

        // 标记其他分组 隐藏的字段
        if (isCustomField(dataKey) && data[dataKey] === false) customHideLayouts.add(getCustomFieldId(dataKey, '_visible'));

        // 兼容visible对应的字段值不存在的情况
        const datakey = dataKey.slice(0, dataKey.lastIndexOf('_visible')); // visible 对应的data key
        if (needSync(datakey, data)) return;
        const { commonlayoutIds: _commonlayoutIds, commonLayoutConfig: _commonLayoutConfig, customLayoutIds: _customLayoutIds, customLayoutIdConfigs: _customLayoutIdConfigs } = changeCommonLayout(datakey, data, content, store, { commonlayoutIds, commonLayoutConfig, customLayoutIds, customLayoutIdConfigs });
        commonlayoutIds = _commonlayoutIds;
        commonLayoutConfig = _commonLayoutConfig;
        customLayoutIds = _customLayoutIds;
        customLayoutIdConfigs = _customLayoutIdConfigs;
      } else if (isSortSetting(dataKey)) {
        // 兼容sort对应的字段值不存在的情况
        const datakey = dataKey.slice(0, dataKey.lastIndexOf('_sort')); // visible 对应的data key
        if (needSync(datakey, data)) return;
        const { commonlayoutIds: _commonlayoutIds, commonLayoutConfig: _commonLayoutConfig, customLayoutIds: _customLayoutIds, customLayoutIdConfigs: _customLayoutIdConfigs } = changeCommonLayout(datakey, data, content, store, { commonlayoutIds, commonLayoutConfig, customLayoutIds, customLayoutIdConfigs });
        commonlayoutIds = _commonlayoutIds;
        commonLayoutConfig = _commonLayoutConfig;
        customLayoutIds = _customLayoutIds;
        customLayoutIdConfigs = _customLayoutIdConfigs;
      }
    })
    let idConfigs: any[] = [];
    customLayoutIdConfigs.forEach((config) => {
      idConfigs = idConfigs.concat(config);
    })
    // 默认布局、数据所有配置存入browserConfigStore
    !isReset && store.browserConfigStore.setState({
      statusDatas,
      disableFieldsKeys,
      itemColMapping,
      disableItemsKeys,
      wholeRowReadOnlyFieldsKeys,
      datas,
      commonLayoutConfig,
      customHideLayouts,
      allFields,
      commonCondition,
    })

    /**
     * 1.布局接口，字段默认值在items内的value设置
     * 2.transCustomLayout的逻辑规定，initForm完全以datas的数据为准，因此会丢失1
     * 3.对于datas内不存在且items内存在的数据，兼容处理
     */
    Object.keys(content?.items).forEach((itemkey) => {
      if (!isEmpty(content?.items[itemkey]?.value) && !needSync(itemkey, datas)) {
        datas = {
          ...datas,
          [itemkey]: content?.items[itemkey]?.value,
        }
      }
    })
    /* ------------------------------------------------------------------------ */
    console.log(datas, disableFieldsKeys);
    return {
      data: datas,
      issetting: true,
      readOnlyItems,
      commonLayoutConfig,
      // customLayoutIds,
      customLayoutIds: idConfigs, // 兼容 sort数据重复的场景
      customHideLayouts,
      layout,
      wholeRowReadOnlyFieldsKeys,
      disableFieldsKeys,
      allFields,
      commonCondition,
    }
  }
  return {
    data: _data,
    issetting: false,
  }
}

/**
 * 其他分组排序
 * @param store 
 * @param customLayoutIds 
 */
const sortCustomLayout = (store: SearchAdvancedStoreType, customLayout: FormCustomLayoutProps, customLayoutIds: string[]) => {
  if (customLayoutIds && customLayoutIds.length > 0) {
    const nowCustomRows: FormLayoutProps[] = [];
    const customRows = customLayout?.otherGroup?.[0];
    if (!customRows) return;
    for (let i = 0; i < customLayoutIds.length; i++) {
      if (!customLayoutIds[i]) continue;
      const cols = customRows.find((col) => col.labelId === customLayoutIds[i]);
      if (cols && !nowCustomRows.find((c) => c.labelId === cols.labelId)) nowCustomRows.push(cols);
    }
    if (nowCustomRows.length > 0) {
      store.formStore.setAllCustomLayout({
        otherGroup: [nowCustomRows]
      }, true);
    }
  }
}

/**
 * 回显高级搜索数据
 * @param content 高级搜索布局数据
 * @param store 
 * @param needTransBrowserConfigLayout （常用分组）是否需要处理布局(隐藏、排序)，有自定义设置常用条件的排序和显影的不需要处理
 * @returns 
 */
export const transBrowserConfigDatas = (content: SearchAdvancedInitAllDatas, store: SearchAdvancedStoreType, needTransBrowserConfigLayout?: boolean, isReset?: boolean) => {
  let { issetting, data, readOnlyItems, commonLayoutConfig, customLayoutIds, customHideLayouts, layout, wholeRowReadOnlyFieldsKeys, disableFieldsKeys, allFields, commonCondition } = transDatas(content.data, content, store, needTransBrowserConfigLayout, isReset);
  if (issetting) {
    // needTransBrowserConfigLayout = true, hideLayouts以data内的visible数据为准
    let hideLayouts: Set<string> = !isReset ?
      new Set(needTransBrowserConfigLayout ? commonLayoutConfig.filter((r: { open: any; }) => !r.open).map((r: { layoutId: any; }) => r.layoutId) : [])
      : store.browserConfigStore.hideLayouts;
    if (commonLayoutConfig?.length > 0) {
      // 常用分组显示隐藏/排序处理
      const { customLayout } = store.transDatasStore.transCustomLayout({
        ...content,
        layout: needTransBrowserConfigLayout ? store.sortCommonCondition(layout, commonLayoutConfig, true) : layout,
        data,
      }, true, readOnlyItems, hideLayouts, customHideLayouts, {
        wholeRowReadOnlyFieldsKeys, disableFieldsKeys, forbidAddCustomLayout: true
      });
      // 其他分组排序
      customLayout && sortCustomLayout(store, customLayout, customLayoutIds);
      // 禁用其他分组条件
      customLayout && doDisabledOtherGroupFields(store, store.formStore.customLayout, disableFieldsKeys);
    } else {
      const { customLayout } = store.transDatasStore.transCustomLayout({ ...content, data, layout }, true, readOnlyItems, hideLayouts, customHideLayouts, {
        wholeRowReadOnlyFieldsKeys, disableFieldsKeys, forbidAddCustomLayout: true
      });
      // 其他分组排序
      customLayout && sortCustomLayout(store, customLayout, customLayoutIds);
      // 禁用其他分组条件
      customLayout && doDisabledOtherGroupFields(store, store.formStore.customLayout, disableFieldsKeys);
    }
    if (!isReset) {
      // 无个人设置项，常用条件设置需以后台设置的顺序为准
      if (needTransBrowserConfigLayout) commonCondition = store.sortCommonCondition([commonCondition], commonLayoutConfig, true)?.[0];
      // 更新allFields
      store.setAllFields(allFields);
      store.formStore.setAllFields(allFields);
      store.setState({
        commonCondition: commonCondition,
        initCommonCondition: commonCondition,
      })
      store.browserConfigStore.setState({
        hideLayouts,
      })
    } else {
      // 禁用为空的情况，确保allFields字段均可用
      const result = allFields?.map((field: any) => ({
        ...field,
        disabled: store.browserConfigStore?.disableFieldsKeys?.has(field.id),
      }))
      store.formStore.setAllFields(result); // 更新form内的allFields，仅处理其他分组的可新增的字段label
    }
    return { content, needInit: false };
  }
  return { content, needInit: true };
}
/**
 * 分组字段只读处理(仅用于其他分组)
 * @param itemKey 
 * @param item 
 * @param readOnlyItems 
 * @returns 
 */
export const transItems = (itemKey: string, item: FormSingleItemProps, readOnlyItems?: Set<string | undefined>, otherConfigs?: AnyObj): FormSingleItemProps => {
  const { wholeRowReadOnlyFieldsKeys } = otherConfigs || {};
  const customIndex = itemKey.indexOf('_customField');
  const labelId = itemKey.slice(customIndex);
  let dataKey = itemKey.slice(0, customIndex);
  let readonly = false;
  if (readOnlyItems && readOnlyItems?.size > 0 && readOnlyItems?.has(itemKey)) readonly = true;
  if (wholeRowReadOnlyFieldsKeys?.has(dataKey)) {
    wholeRowReadOnlyFieldsKeys.get(dataKey)?.forEach((key: string) => {
      readOnlyItems?.has(`${key}${labelId}`) && (readonly = true);
    });
  }
  if (readonly) {
    return {
      ...item,
      readOnly: true,
      required: false,
      rules: '',
      otherParams: {
        ...item?.otherParams,
        readOnly: true,
        required: false,
        rules: '',
      }
    }
  }
  return item;
}

export const transAllItems = (items: FormItemProps, readOnlyItems?: Set<string | undefined>): FormItemProps => {
  let result = {};
  Object.keys(items).forEach((itemKey) => {
    result = {
      ...result,
      [`${itemKey}`]: transItems(itemKey, items[itemKey], readOnlyItems),
    }
  })
  return result;
}

export const transCommonGroupItems = (readOnlyItems?: Set<string | undefined>, formStore?: FormStoreType, otherConfigs?: AnyObj) => {
  /*
    initForm之后更新常用分组的items配置，确保不影响initItems数据，避免其他分组新增条件时，带上了常用分组对应字段的只读配置
    对标：Form -> Store -> addCustomLayout方法
  */
  const { wholeRowReadOnlyFieldsKeys } = otherConfigs || {};
  readOnlyItems?.forEach((itemKey) => {
    const isCommon = itemKey && itemKey?.indexOf('_customField') < 0;
    if (itemKey && isCommon) {
      let readOnlyKeys = [itemKey];
      if (wholeRowReadOnlyFieldsKeys?.has(itemKey)) {
        readOnlyKeys = readOnlyKeys.concat(wholeRowReadOnlyFieldsKeys.get(itemKey));
      }
      readOnlyKeys.forEach((k) => formStore?.setItemProps(k, {
        readOnly: true, required: false, rules: '',
        otherParams: {
          ...formStore?.items?.[k],
          readOnly: true, required: false, rules: '',
        }
      }));
    }
  })
}

// 只读的字段不可再更改字段类型(其他分组)
export const customRenderLabel = (store: SearchAdvancedStoreType, id: string, label?: string, content?: JSX.Element, props?: FormItemSingleProps) => {
  const { browserConfigStore: { readOnlyItems } } = store;
  const items = props?.item;
  if (props?.custom && items && Object.keys(items)?.find((itemKey: string) => readOnlyItems.has(itemKey))) {
    return <span className={`${formItemClsPrefix}-label-span`} title={label}>{label}</span>
  };
  return content;
}
export const customRenderWrapper = (prefix?: string, store?: SearchAdvancedStoreType, props?: FormItemWrapperProps, com?: ReactNode, otherParams?: AnyObj) => {
  const { browserConfigStore } = store || {};
  const { readOnlyItems } = browserConfigStore || {};
  const items = props?.item;
  if (items && readOnlyItems && Object.keys(items)?.find((itemKey: string) => readOnlyItems.has(itemKey))) {
    return <div className={`${prefix}-browserConfig-wrapper`}>{com}</div>;
  };
  return com;
}
export default transBrowserConfigDatas;

export const getLayoutFromItemKey = (store: SearchAdvancedStoreType, layout: FormLayoutProps | null, otherParams?: AnyObj) => {
  const {
    browserConfigStore: {
      hasDefaultConfig, readOnlyItems
    },
    isMobile
  } = store;
  if (hasDefaultConfig) {
    const items = layout?.items;
    if (items && readOnlyItems && items?.find((itemKey: string) => readOnlyItems.has(`${itemKey}_${otherParams?.labelId}`))) {
      const prefix = isMobile ? mSearchAdvancedClsPrefix : searchAdvancedClsPrefix;
      return {
        ...layout,
        wrapClassName: classnames(layout?.wrapClassName, `${prefix}-browserConfig-form-item-readonly`)
      }
    };
  }
  return layout;
}